import { useEffect, useMemo, useState } from "react";
import { ApiError, api, type Product } from "./api";
import { FindProductPicture } from "./FindProductPicture";
import { productName } from "./productName";
import { Alert, Modal, Working } from "./ui";

/**
 * Pictures for the products that have none, one after another (D, 14 October 2026: "for
 * this singles, I have gathered the official name. Search online and then let me choose
 * photo"). Each search is the one a product has on its own page; the picture chosen is
 * saved straight away and the next product opens, so 28 singles are 28 clicks.
 */
export function PictureQueue({ onClose, onDone }: { readonly onClose: () => void; readonly onDone: () => void }): React.ReactElement {
  const [products, setProducts] = useState<Product[] | undefined>();
  const [onlySingles, setOnlySingles] = useState(true);
  const [open, setOpen] = useState<Product | undefined>();
  const [saved, setSaved] = useState<string[]>([]);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [problem, setProblem] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void api.products().then((result) => setProducts(result.products.filter((product) => product.isActive && !product.photoId)));
  }, []);

  const waiting = useMemo(
    () => (products ?? []).filter((product) => !saved.includes(product.id) && (!onlySingles || product.itemType === "single" || product.category === "Singles")),
    [products, saved, onlySingles],
  );
  /** The next one after this, passing over any skipped this time. */
  const after = (product: Product): Product | undefined => {
    const rest = waiting.filter((one) => one.id !== product.id && !skipped.includes(one.id));
    const at = waiting.findIndex((one) => one.id === product.id);
    return rest.find((one) => waiting.indexOf(one) > at) ?? undefined;
  };

  return (
    <Modal
      open
      size="lg"
      title="Pictures for products without one"
      onClose={() => {
        if (saved.length) onDone();
        onClose();
      }}
      footer={
        <button
          type="button"
          className="ti-btn ti-btn-light"
          onClick={() => {
            if (saved.length) onDone();
            onClose();
          }}
        >
          Close
        </button>
      }
    >
      <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50">
        Press Find picture. The search uses the product's name, so a full name such as "JP McDonald's Promo Wooper #022/P" finds the
        right card. Choose the picture, and it is saved at once and the next product opens. Skip one to come back to it later.
      </p>
      <label className="flex items-center gap-2 mb-3 text-[0.813rem]">
        <input type="checkbox" className="form-check-input" checked={onlySingles} onChange={(event) => setOnlySingles(event.target.checked)} />
        Singles only
      </label>
      {problem ? <Alert tone="danger">{problem}</Alert> : null}
      {saved.length ? <p className="text-success text-[0.813rem]">{saved.length} {saved.length === 1 ? "picture" : "pictures"} saved.</p> : null}
      {!products ? (
        <Working label="Loading products" />
      ) : waiting.length === 0 ? (
        <p className="mb-0 font-semibold">Every {onlySingles ? "single" : "product"} has a picture.</p>
      ) : (
        <div className="max-h-[26rem] overflow-y-auto border border-defaultborder rounded-md">
          {waiting.map((product) => (
            <div key={product.id} className="flex items-center gap-3 px-3 py-2 border-b border-defaultborder">
              <span className="flex-1 text-[0.875rem]">{productName(product)}</span>
              {skipped.includes(product.id) ? <span className="text-[0.75rem] text-[#8c9097]">Skipped</span> : null}
              <button type="button" className="ti-btn ti-btn-primary-full !py-1 !px-3 !text-[0.75rem] !mb-0" disabled={saving} onClick={() => setOpen(product)}>
                <i className="ri-search-line" /> Find picture
              </button>
            </div>
          ))}
        </div>
      )}
      {open ? (
        <FindProductPicture
          key={open.id}
          savesAtOnce
          product={open}
          onClose={() => {
            setSkipped((was) => (was.includes(open.id) ? was : [...was, open.id]));
            setOpen(undefined);
          }}
          onChosen={(taken) => {
            const product = open;
            const next = after(product);
            setSaving(true);
            setProblem(undefined);
            setOpen(undefined);
            void api
              .setProductPicture(product.id, taken.contentType, taken.data)
              .then(() => {
                setSaved((was) => [...was, product.id]);
                if (next) setOpen(next);
              })
              .catch((error: unknown) => setProblem(error instanceof ApiError ? error.message : `Could not save the picture for ${productName(product)}`))
              .finally(() => setSaving(false));
          }}
        />
      ) : null}
    </Modal>
  );
}
